import React, { useCallback, useEffect, useState } from "react";

// ──────────────────────────────────────────────────────
// AdminClassifyHistory.jsx — 분류 이력 (어떤 사용자가 무엇을 세번분류 했는지)
//   GET /api/hs-classify/recent?limit=N  (hs_classify_audit 최근순, user_id 포함)
//   사용자 · 입력품명 → 예측 HSK / 신뢰도 / 정정여부 / pipeline / 응답시간.
//   ※ 어드민 전용 — user_id(사용자 식별) 노출은 어드민 화면에서만.
// ──────────────────────────────────────────────────────

const fmtDateTime = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v).slice(0, 16);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch {
    return String(v).slice(0, 16);
  }
};

const SYSTEM_PIPELINE = "official-frame-v2";

const buildUserMap = (users) => {
  const out = {};
  (Array.isArray(users) ? users : []).forEach((user) => {
    const id = String(user?.USER_ID || "").trim();
    if (id) out[id.toLowerCase()] = user;
  });
  return out;
};

export default function AdminClassifyHistory({ API_BASE_URL }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(100);
  const [userFilter, setUserFilter] = useState("");
  const [userMap, setUserMap] = useState({});
  const [includeSystem, setIncludeSystem] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const adminToken = localStorage.getItem("admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    const recent = fetch(
      `${API_BASE_URL}/api/hs-classify/recent?limit=${limit}&include_system=${includeSystem}`,
      { headers },
    ).then((r) =>
      r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status)),
    );
    const users = fetch(`${API_BASE_URL}/api/admin/users`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    Promise.all([recent, users])
      .then(([history, userResponse]) => {
        setRows(Array.isArray(history?.rows) ? history.rows : []);
        setUserMap(buildUserMap(userResponse?.data));
      })
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [API_BASE_URL, includeSystem, limit]);

  useEffect(() => load(), [load]);

  const uf = userFilter.trim().toLowerCase();
  const filtered = uf
    ? rows.filter((r) => {
        const user = userMap[String(r.user_id || "").toLowerCase()] || {};
        return [r.user_id, user.USER_NAME, user.GROUP_ID, r.pipeline]
          .some((value) => String(value || "").toLowerCase().includes(uf));
      })
    : rows;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-800">
          분류 이력{" "}
          <span className="text-sm font-normal text-gray-400">
            최근 {filtered.length}건 — 어떤 사용자가 입력한 품명 → 나온 세번(HSK)
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <input
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="사용자 필터"
            className="border border-gray-300 rounded px-2 py-1 text-sm w-36"
          />
          <label
            className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap"
            title="공식 결정 프레임의 품명 비저장 내부 관측을 함께 표시합니다."
          >
            <input
              type="checkbox"
              checked={includeSystem}
              onChange={(e) => setIncludeSystem(e.target.checked)}
            />
            시스템 관측 포함
          </label>
          <label className="text-sm text-gray-600">
            건수
            <select
              className="ml-1 border border-gray-300 rounded px-2 py-1 text-sm"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <button
            onClick={load}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          >
            새로고침
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">불러오는 중…</div>
      ) : error ? (
        <div className="text-red-500 text-sm">불러오기 실패: {error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-400 text-sm">분류 이력이 없습니다.</div>
      ) : (
        <div>
          {includeSystem && (
            <div className="mb-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
              공식 프레임 관측은 실제 품명을 저장하지 않고 단방향 SHA-256 식별값만
              남기는 내부 품질 기록입니다. 이 식별값으로 원래 품명을 복원할 수 없습니다.
            </div>
          )}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">시각</th>
                <th className="px-3 py-2 text-left font-semibold">사용자</th>
                <th className="px-3 py-2 text-left font-semibold">입력 품명</th>
                <th className="px-3 py-2 text-left font-semibold">챕터</th>
                <th className="px-3 py-2 text-left font-semibold">예측 HSK</th>
                <th className="px-3 py-2 text-left font-semibold">신뢰도</th>
                <th className="px-3 py-2 text-left font-semibold">정정</th>
                <th className="px-3 py-2 text-left font-semibold">pipeline</th>
                <th className="px-3 py-2 text-right font-semibold">응답(ms)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((it) => {
                const corrected =
                  it.reviewed_hsk && it.reviewed_hsk !== it.predicted_hsk;
                const isSystem = it.pipeline === SYSTEM_PIPELINE;
                const user = userMap[String(it.user_id || "").toLowerCase()];
                return (
                  <tr key={it.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 whitespace-nowrap text-gray-500 font-mono text-xs">
                      {fmtDateTime(it.requested_at)}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {isSystem ? (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 font-sans text-blue-700">
                          시스템
                        </span>
                      ) : it.user_id ? (
                        <span className="font-sans" title={`사용자 ID: ${it.user_id}`}>
                          <span className="font-medium text-gray-800">
                            {user?.USER_NAME || it.user_id}
                          </span>
                          {user?.USER_NAME && (
                            <span className="ml-1 text-gray-400">({it.user_id})</span>
                          )}
                        </span>
                      ) : (
                        <span
                          className="font-sans text-gray-400"
                          title="비로그인 또는 사용자 토큰이 전달되지 않은 과거 기록"
                        >
                          미식별
                        </span>
                      )}
                    </td>
                    <td
                      className="px-3 py-1.5 max-w-[300px] truncate"
                      title={it.line_item}
                    >
                      {isSystem ? (
                        <span>
                          <span className="font-medium text-gray-700">공식 프레임 내부 관측</span>
                          <span className="ml-2 font-mono text-xs text-gray-400">
                            {String(it.line_item || "").replace(/^sha256:/, "").slice(0, 10)}…
                          </span>
                        </span>
                      ) : (
                        it.line_item
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600">
                      {it.request_chapter}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-gray-800">
                      {it.predicted_hsk || it.predicted_hs4 || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600">
                      {it.confidence || "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      {corrected ? (
                        <span
                          className="text-amber-600 font-medium"
                          title={`정정: ${it.reviewed_hsk}`}
                        >
                          정정됨
                        </span>
                      ) : it.requires_review ? (
                        <span className="text-gray-400">검토요</span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500 text-xs">
                      {isSystem ? "공식 프레임" : it.pipeline || ""}
                    </td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono text-xs">
                      {it.response_time_ms ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
